import type { GroupedPoll, PollResponse } from '@tunasong/schemas'
import { Button, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import {
  getGroupOptionIndices,
  getGroupProgress,
  getGroupVoteCounts,
  getGroupVoters,
  getVotableGroups,
  rankGroupOptions,
} from '../groups.js'
import { WithOptionAudio } from '../option-audio.js'
import { PollOptionName } from '../poll-option-name.js'
import Progress from '../progress.js'
import { GroupSection } from './group-section.js'
import css from './grouped.module.css'

export interface GroupedPollResultProps {
  poll: GroupedPoll
  responses: PollResponse[]
}

/**
 * The sentence a closed grouped poll exists to say: each group, and what won it. Winners keep their Play control,
 * so the result doubles as a listening list. The full tallies are one click away, not in the way.
 */
export const GroupedPollResult: FC<GroupedPollResultProps> = props => {
  const { poll, responses } = props
  const [showAll, setShowAll] = useState(false)
  const groups = getVotableGroups(poll)

  return (
    <>
      <ul aria-label="Winners" className={css.results}>
        {groups.map(group => {
          const winners = getGroupOptionIndices(poll, group.id).filter(idx => poll.finalOption?.[idx])
          const counts = getGroupVoteCounts(poll, responses, group.id)
          const voters = getGroupVoters(poll, responses, group.id).length
          return (
            <li key={group.id} className={css.resultRow}>
              <Typography variant="subtitle2" component="span">
                {group.name}
              </Typography>
              {winners.length === 0 ? (
                <Typography color="textSecondary">No winner</Typography>
              ) : (
                winners.map(idx => (
                  <WithOptionAudio key={idx} option={poll.pollOptions[idx]}>
                    <span className={css.winner}>
                      <PollOptionName option={poll.pollOptions[idx]} withAudio={false} />
                      <Typography variant="caption" color="textSecondary">
                        {`${counts.get(idx) ?? 0} of ${voters}`}
                      </Typography>
                    </span>
                  </WithOptionAudio>
                ))
              )}
            </li>
          )
        })}
      </ul>
      <Button size="small" onClick={() => setShowAll(shown => !shown)}>
        {showAll ? 'Hide all results' : 'Show all results'}
      </Button>
      {showAll
        ? groups.map(group => {
            const ranked = rankGroupOptions(poll, responses, group.id)
            return (
              <GroupSection key={group.id} name={group.name} order={ranked}>
                {ranked.map(idx => (
                  <div key={idx} data-reorder-key={idx} className={css.resultRow}>
                    <PollOptionName option={poll.pollOptions[idx]} />
                    <Progress value={getGroupProgress(poll, responses, idx)} selected={poll.finalOption?.[idx]} />
                  </div>
                ))}
              </GroupSection>
            )
          })
        : null}
    </>
  )
}

export default GroupedPollResult
